/**
 * DocumentPreviewModal.js
 * JobEng — In-app document preview with audit logging
 *
 * Props:
 *   visible    boolean
 *   onClose    () => void
 *   document   object  (id, name, type, location, etc.)
 *   companyId  number
 *
 * What it does:
 *   - Authenticated download of the file to local cache (expo-file-system)
 *   - Inline preview for images
 *   - "Open externally" for PDFs and other binaries (expo-sharing / expo-web-browser)
 *   - Logs a `preview` event to the audit trail on open
 *   - Logs a `download` event when user taps Save / Open
 *
 * Path:
 *   app/screens/business/documents/DocumentPreviewModal.js
 *
 * Required Expo modules (verify installed):
 *   expo-file-system, expo-sharing, expo-web-browser
 *   (expo-sharing is already imported in DocumentsControlScreen.js)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, Modal, TouchableOpacity, Image,
  ActivityIndicator, ScrollView, Dimensions, Platform, Alert,
} from 'react-native';
import { SafeAreaView }           from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Ionicons }               from '@expo/vector-icons';
import { LinearGradient }         from 'expo-linear-gradient';
import AsyncStorage               from '@react-native-async-storage/async-storage';
import axios                      from 'axios';
import * as FileSystem            from 'expo-file-system';
import * as Sharing               from 'expo-sharing';
import * as WebBrowser            from 'expo-web-browser';

const { width, height } = Dimensions.get('window');
const IS_PROMAX = width >= 430;
const HPAD      = IS_PROMAX ? 18 : 14;
const CARD_R    = IS_PROMAX ? 16 : 12;
const FS_XS     = IS_PROMAX ? 11 : 10;
const FS_SM     = IS_PROMAX ? 13 : 11;
const FS_BASE   = IS_PROMAX ? 15 : 13;
const FS_MD     = IS_PROMAX ? 16 : 14;
const FS_LG     = IS_PROMAX ? 18 : 16;
const FS_XL     = IS_PROMAX ? 20 : 17;

const C = {
  primary:'#0e4e96', accent:'#19abb5', gold:'#fdb913',
  success:'#10b981', warning:'#f59e0b', danger:'#dc2626', purple:'#8b5cf6',
  bg:'#0f172a', card:'#fff', border:'#e2e8f0',
  dark:'#0f172a', mid:'#475569', light:'#94a3b8',
};

const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'x-api-key': API_KEY, 'Content-Type': 'application/json' },
  timeout: 12000,
});
api.interceptors.request.use(async (config) => {
  try {
    const token = (await AsyncStorage.getItem('auth_token'))
              || (await AsyncStorage.getItem('userToken'))
              || (await AsyncStorage.getItem('token'));
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
    const bizId = await AsyncStorage.getItem('company_id');
    if (bizId) {
      config.params = config.params || {};
      config.params.company_id = parseInt(bizId);
    }
  } catch {}
  return config;
});

// ── Helpers ─────────────────────────────────────────────────────────────────
const TYPE_INFO = {
  pdf:   { icon:'file-pdf-box',          color:'#dc2626', label:'PDF Document',  inline:false },
  docx:  { icon:'file-word-box',         color:'#2196f3', label:'Word Document', inline:false },
  xlsx:  { icon:'file-excel-box',        color:'#10b981', label:'Excel Sheet',   inline:false },
  pptx:  { icon:'file-powerpoint-box',   color:'#f59e0b', label:'Presentation',  inline:false },
  dwg:   { icon:'vector-square',         color:'#8b5cf6', label:'CAD Drawing',   inline:false },
  dxf:   { icon:'vector-line',           color:'#19abb5', label:'CAD Drawing',   inline:false },
  img:   { icon:'file-image',            color:'#607d8b', label:'Image',         inline:true  },
  zip:   { icon:'folder-zip',            color:'#0e4e96', label:'Archive',       inline:false },
  txt:   { icon:'file-document-outline', color:'#94a3b8', label:'Text File',     inline:false },
  mp4:   { icon:'file-video',            color:'#e91e63', label:'Video',         inline:false },
};
const infoFor = (t) => TYPE_INFO[t] || TYPE_INFO.pdf;

const isImageType = (doc) => {
  if (!doc) return false;
  if (doc.type === 'img') return true;
  const loc = (doc.location || doc.url || '').toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|heic)$/.test(loc);
};

const fmtBytes = (b) => {
  if (!b) return '—';
  const n = typeof b === 'number' ? b : parseInt(b, 10);
  if (!n) return '—';
  if (n < 1e3)  return `${n} B`;
  if (n < 1e6)  return `${(n/1e3).toFixed(1)} KB`;
  if (n < 1e9)  return `${(n/1e6).toFixed(1)} MB`;
  return `${(n/1e9).toFixed(2)} GB`;
};

const buildAuthHeaders = async () => {
  const token = (await AsyncStorage.getItem('auth_token'))
            || (await AsyncStorage.getItem('userToken'))
            || (await AsyncStorage.getItem('token'));
  const headers = { 'x-api-key': API_KEY };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

const buildFileUrl = (doc) => {
  if (!doc) return null;
  // Prefer authenticated download endpoint
  if (doc.id) return `${API_BASE}/api/docs/${doc.id}/download`;
  // Fall back to direct location if no id
  if (doc.location) {
    if (/^https?:/.test(doc.location)) return doc.location;
    return `${API_BASE}${doc.location.startsWith('/') ? '' : '/'}${doc.location}`;
  }
  return null;
};

// Audit logger — uses the new POST /api/docs/:id/audit route
const logAudit = async (docId, action, meta = {}) => {
  try {
    if (!docId) return;
    await api.post(`/api/docs/${docId}/audit`, { action, meta });
  } catch (err) {
    // Don't surface — audit failures must not block preview
    console.warn('[preview] audit log failed:', err.message);
  }
};

// ── Main ────────────────────────────────────────────────────────────────────
export default function DocumentPreviewModal({ visible, onClose, document, companyId }) {
  const [loading,   setLoading]   = useState(false);
  const [localUri,  setLocalUri]  = useState(null);
  const [error,     setError]     = useState(null);
  const [progress,  setProgress]  = useState(0);

  const type    = document?.type || 'pdf';
  const info    = infoFor(type);
  const isImage = isImageType(document);

  // Reset state on open/close and log 'preview' event when opened
  useEffect(() => {
    if (visible && document?.id) {
      setLoading(false); setLocalUri(null); setError(null); setProgress(0);
      logAudit(document.id, 'preview', { type, name: document.name });
      // Auto-download images for inline preview
      if (isImage) downloadFile(false);
    }
    if (!visible) {
      setLoading(false); setLocalUri(null); setError(null); setProgress(0);
    }
  }, [visible, document?.id]);

  /**
   * Download the file to local cache, with progress.
   * @param logDownload — true if this is a user-initiated download (logs 'download')
   *                     false if it's an internal preview fetch (already logged 'preview')
   */
  const downloadFile = useCallback(async (logDownload) => {
    if (!document?.id) {
      setError('Missing document id');
      return null;
    }
    setLoading(true); setError(null); setProgress(0);

    try {
      const headers = await buildAuthHeaders();
      const url = buildFileUrl(document);
      if (!url) throw new Error('No download URL resolvable');

      // Sanitise extension
      const safeName = String(document.name || `doc_${document.id}`)
        .replace(/[^a-zA-Z0-9._-]/g, '_')
        .slice(0, 80);
      const ext = (type === 'img'
        ? ((document.location || '').match(/\.(png|jpe?g|gif|webp)$/i)?.[1] || 'jpg')
        : type);
      const cleanName = /\.[a-z0-9]+$/i.test(safeName) ? safeName : `${safeName}.${ext}`;
      const fileUri = `${FileSystem.cacheDirectory}preview_${document.id}_${cleanName}`;

      // Append company_id as query param for backend filtering
      const dlUrl = `${url}${url.includes('?') ? '&' : '?'}company_id=${companyId || ''}`;

      const downloadResumable = FileSystem.createDownloadResumable(
        dlUrl,
        fileUri,
        { headers },
        (p) => {
          if (p.totalBytesExpectedToWrite > 0) {
            setProgress(p.totalBytesWritten / p.totalBytesExpectedToWrite);
          }
        }
      );
      const result = await downloadResumable.downloadAsync();
      if (!result?.uri) throw new Error('Download returned no URI');

      setLocalUri(result.uri);
      if (logDownload) {
        logAudit(document.id, 'download', { via: 'preview_modal', bytes: progress * 1 });
      }
      return result.uri;
    } catch (err) {
      console.error('[preview] download failed:', err);
      setError(err.message || 'Download failed');
      return null;
    } finally {
      setLoading(false);
    }
  }, [document, companyId, type, progress]);

  const handleOpenExternally = useCallback(async () => {
    const uri = localUri || (await downloadFile(true));
    if (!uri) return;
    try {
      const available = await Sharing.isAvailableAsync();
      if (available) {
        await Sharing.shareAsync(uri, {
          dialogTitle: document?.name || 'Document',
          mimeType: type === 'pdf' ? 'application/pdf' : undefined,
        });
      } else {
        // Last-resort: try opening in in-app browser
        await WebBrowser.openBrowserAsync(uri);
      }
    } catch (err) {
      Alert.alert('Open failed', err.message || 'Could not open the document');
    }
  }, [localUri, downloadFile, document, type]);

  const handleSaveCopy = useCallback(async () => {
    const uri = localUri || (await downloadFile(true));
    if (!uri) return;
    Alert.alert(
      'Saved to cache',
      `File saved at:\n${uri}\n\nTap "Open externally" to save to your device or share.`,
    );
  }, [localUri, downloadFile]);

  if (!document) return null;

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose} statusBarTranslucent>
      <SafeAreaView style={s.root} edges={['top']}>
        {/* Header */}
        <LinearGradient colors={[C.primary, C.accent]} style={s.header}>
          <TouchableOpacity onPress={onClose} style={s.headerBtn} hitSlop={{ top:10, bottom:10, left:10, right:10 }}>
            <Ionicons name="chevron-back" size={26} color="#fff" />
          </TouchableOpacity>
          <View style={{ flex:1 }}>
            <Text style={s.headerTitle} numberOfLines={1}>{document.name || 'Document'}</Text>
            <Text style={s.headerSub} numberOfLines={1}>
              {info.label} · {fmtBytes(document.sizeBytes || document.size)}
              {document.version ? ` · v${document.version}` : ''}
            </Text>
          </View>
          <TouchableOpacity onPress={handleOpenExternally} style={s.headerBtn} hitSlop={{ top:10, bottom:10, left:10, right:10 }}>
            <MaterialCommunityIcons name="open-in-new" size={22} color="#fff" />
          </TouchableOpacity>
        </LinearGradient>

        {/* Body */}
        {isImage ? (
          <View style={s.imageWrap}>
            {loading ? (
              <View style={s.center}>
                <ActivityIndicator size="large" color="#fff" />
                <Text style={s.loadTxt}>Loading image… {Math.round(progress * 100)}%</Text>
              </View>
            ) : error ? (
              <View style={s.center}>
                <MaterialCommunityIcons name="image-broken-variant" size={64} color="#fff" />
                <Text style={s.errTitleLight}>Couldn't load image</Text>
                <Text style={s.errMsgLight}>{error}</Text>
                <TouchableOpacity style={s.retryBtn} onPress={() => downloadFile(false)} activeOpacity={0.85}>
                  <Text style={s.retryBtnTxt}>Retry</Text>
                </TouchableOpacity>
              </View>
            ) : localUri ? (
              <ScrollView
                contentContainerStyle={{ minHeight: height - 200, justifyContent:'center', alignItems:'center' }}
                maximumZoomScale={4} minimumZoomScale={1}
              >
                <Image
                  source={{ uri: localUri }}
                  style={{ width: width, height: height * 0.7 }}
                  resizeMode="contain"
                />
              </ScrollView>
            ) : null}
          </View>
        ) : (
          /* Non-image: hero card + action buttons */
          <ScrollView style={s.bodyScroll} contentContainerStyle={s.body}>
            <View style={s.heroCard}>
              <View style={[s.heroIcon, { backgroundColor: `${info.color}15` }]}>
                <MaterialCommunityIcons name={info.icon} size={IS_PROMAX ? 72 : 64} color={info.color} />
              </View>
              <Text style={s.heroName} numberOfLines={2}>{document.name}</Text>
              <Text style={s.heroLabel}>{info.label}</Text>

              <View style={s.heroChips}>
                <View style={s.heroChip}>
                  <Text style={s.heroChipTxt}>{(type || '').toUpperCase()}</Text>
                </View>
                <View style={s.heroChip}>
                  <Text style={s.heroChipTxt}>{fmtBytes(document.sizeBytes || document.size)}</Text>
                </View>
                {document.version ? (
                  <View style={s.heroChip}>
                    <Text style={s.heroChipTxt}>v{document.version}</Text>
                  </View>
                ) : null}
              </View>
            </View>

            {error && (
              <View style={s.errBox}>
                <MaterialCommunityIcons name="alert-circle-outline" size={28} color={C.danger} />
                <Text style={s.errTxt}>{error}</Text>
              </View>
            )}

            {loading && (
              <View style={s.progressBox}>
                <ActivityIndicator color={C.primary} />
                <Text style={s.progressTxt}>Downloading… {Math.round(progress * 100)}%</Text>
                <View style={s.progressTrack}>
                  <View style={[s.progressFill, { width: `${Math.round(progress * 100)}%` }]} />
                </View>
              </View>
            )}

            <View style={s.actionsRow}>
              <TouchableOpacity
                style={[s.primaryBtn, loading && s.btnDisabled]}
                onPress={handleOpenExternally}
                disabled={loading}
                activeOpacity={0.85}
              >
                <MaterialCommunityIcons name="open-in-new" size={20} color="#fff" />
                <Text style={s.primaryBtnTxt}>Open / Share</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[s.secondaryBtn, loading && s.btnDisabled]}
                onPress={handleSaveCopy}
                disabled={loading}
                activeOpacity={0.85}
              >
                <MaterialCommunityIcons name="download" size={20} color={C.primary} />
                <Text style={s.secondaryBtnTxt}>Download</Text>
              </TouchableOpacity>
            </View>

            {/* Metadata */}
            <View style={s.metaBox}>
              <Text style={s.metaTitle}>Document Information</Text>
              <MetaRow label="Reference" value={document.reference} />
              <MetaRow label="Category"  value={document.category} />
              <MetaRow label="Author"    value={document.author} />
              <MetaRow label="Department"value={document.department} />
              <MetaRow label="Created"   value={document.createdDate} />
              <MetaRow label="Modified"  value={document.modifiedDate} />
              <MetaRow label="Expires"   value={document.expiryDate} />
              <MetaRow label="Status"    value={document.status} />
              <MetaRow label="Access"    value={document.accessLevel} />
              {document.checksum ? <MetaRow label="Checksum" value={document.checksum} /> : null}
            </View>
          </ScrollView>
        )}
      </SafeAreaView>
    </Modal>
  );
}

const MetaRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <View style={s.metaRow}>
      <Text style={s.metaLabel}>{label}</Text>
      <Text style={s.metaVal} numberOfLines={1}>{String(value)}</Text>
    </View>
  );
};

const s = StyleSheet.create({
  root:        { flex:1, backgroundColor:'#f1f5f9' },
  header:      { flexDirection:'row', alignItems:'center', paddingHorizontal:HPAD, paddingTop:Platform.OS === 'android' ? 16 : 8, paddingBottom:14, gap:6 },
  headerBtn:   { width:36, height:36, justifyContent:'center', alignItems:'center' },
  headerTitle: { fontSize:FS_LG, fontWeight:'700', color:'#fff', letterSpacing:-0.2 },
  headerSub:   { fontSize:FS_SM, color:'rgba(255,255,255,0.85)', marginTop:2 },

  imageWrap:   { flex:1, backgroundColor:'#0f172a' },
  bodyScroll:  { flex:1 },
  body:        { padding:HPAD, paddingBottom:40 },

  heroCard:    { backgroundColor:C.card, borderRadius:CARD_R, padding:IS_PROMAX?24:20, alignItems:'center', borderWidth:1, borderColor:C.border, marginBottom:14 },
  heroIcon:    { width:IS_PROMAX?120:104, height:IS_PROMAX?120:104, borderRadius:24, justifyContent:'center', alignItems:'center', marginBottom:14 },
  heroName:    { fontSize:FS_XL, fontWeight:'800', color:C.dark, textAlign:'center', letterSpacing:-0.3, marginBottom:6 },
  heroLabel:   { fontSize:FS_BASE, color:C.mid, fontWeight:'600', marginBottom:14 },
  heroChips:   { flexDirection:'row', flexWrap:'wrap', gap:8, justifyContent:'center' },
  heroChip:    { paddingHorizontal:12, paddingVertical:6, backgroundColor:'#f1f5f9', borderRadius:14, borderWidth:1, borderColor:C.border },
  heroChipTxt: { fontSize:FS_SM, fontWeight:'700', color:C.mid, letterSpacing:0.3 },

  actionsRow:  { flexDirection:'row', gap:10, marginBottom:14 },
  primaryBtn:  { flex:2, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8, backgroundColor:C.primary, paddingVertical:IS_PROMAX?14:12, borderRadius:CARD_R },
  primaryBtnTxt:{ fontSize:FS_MD, fontWeight:'700', color:'#fff' },
  secondaryBtn:{ flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:6, backgroundColor:'#fff', paddingVertical:IS_PROMAX?14:12, borderRadius:CARD_R, borderWidth:1, borderColor:C.primary },
  secondaryBtnTxt:{ fontSize:FS_MD, fontWeight:'700', color:C.primary },
  btnDisabled: { opacity:0.5 },

  progressBox: { backgroundColor:C.card, borderRadius:CARD_R, padding:14, alignItems:'center', gap:8, marginBottom:14, borderWidth:1, borderColor:C.border },
  progressTxt: { fontSize:FS_SM, color:C.mid, fontWeight:'600' },
  progressTrack:{ width:'100%', height:6, backgroundColor:'#f1f5f9', borderRadius:3, overflow:'hidden' },
  progressFill:{ height:'100%', backgroundColor:C.accent, borderRadius:3 },

  metaBox:     { backgroundColor:C.card, borderRadius:CARD_R, padding:IS_PROMAX?16:13, borderWidth:1, borderColor:C.border },
  metaTitle:   { fontSize:FS_XS, fontWeight:'700', color:C.mid, marginBottom:10, textTransform:'uppercase', letterSpacing:0.7 },
  metaRow:     { flexDirection:'row', justifyContent:'space-between', paddingVertical:9, borderBottomWidth:1, borderBottomColor:'#f8fafc' },
  metaLabel:   { fontSize:FS_BASE, color:C.mid },
  metaVal:     { fontSize:FS_BASE, color:C.dark, fontWeight:'600', maxWidth:'60%', textAlign:'right' },

  errBox:      { flexDirection:'row', alignItems:'center', gap:10, backgroundColor:'#fef2f2', borderRadius:CARD_R, padding:12, marginBottom:14, borderWidth:1, borderColor:'#fecaca' },
  errTxt:      { flex:1, fontSize:FS_SM, color:C.danger, fontWeight:'600' },

  center:      { flex:1, justifyContent:'center', alignItems:'center', padding:24, gap:10 },
  loadTxt:     { fontSize:FS_BASE, color:'#fff', fontWeight:'600' },
  errTitleLight:{ fontSize:FS_LG, fontWeight:'700', color:'#fff' },
  errMsgLight: { fontSize:FS_BASE, color:'rgba(255,255,255,0.8)', textAlign:'center' },
  retryBtn:    { marginTop:8, paddingHorizontal:24, paddingVertical:11, borderRadius:CARD_R, backgroundColor:C.accent },
  retryBtnTxt: { color:'#fff', fontSize:FS_MD, fontWeight:'700' },
});
